import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import { enumLabel } from "@/i18n/enumLabels";
import {
  SERVICE_KIND_LABELS,
  COST_SCOPE_LABELS,
  type ServiceKind,
  type CostScope,
  SERVICE_KINDS,
  COST_SCOPES,
} from "@ckgo2/shared/types";
import { formatPrice } from "@ckgo2/shared/utils";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import { Plus, Package, Building2 } from "lucide-react";

interface ComponentRow {
  id: string;
  supplierId?: string | null;
  destinationId?: string | null;
  name: string;
  kind: ServiceKind;
  sellPrice: number;
  cost: number;
  currency: string;
  costScope: CostScope;
  isActive: boolean;
  supplierName?: string | null;
  destinationName?: string | null;
}

interface FacetedComponents {
  data: ComponentRow[];
  total: number;
  page: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  success: true;
}

interface SupplierLite {
  id: string;
  name: string;
}

export default function SupplierComponentsPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["supplierComponents", qs],
    queryFn: () => api.get<FacetedComponents>(`/api/admin/supplier-components?${qs}`),
  });

  const { data: destData } = useAdminDestinationList();
  const { data: supData } = useQuery({
    queryKey: ["suppliers", "lookup"],
    queryFn: () => api.get<{ data: SupplierLite[] }>("/api/admin/suppliers?limit=100"),
  });

  const destName = (id: string) => destData?.data.find((d) => d.id === id)?.name ?? id;
  const supName = (id: string) => supData?.data.find((s) => s.id === id)?.name ?? id;

  const rows = data?.data ?? [];
  const facets = data?.facets ?? {};
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 25;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("opsAdmin.components.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("opsAdmin.components.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/supplier-components/new" className="btn-primary">
            <Plus className="h-5 w-5" />
            {t("opsAdmin.components.new")}
          </Link>
          <Link to="/suppliers" className="btn-ghost">
            <Building2 className="h-5 w-5" />
            {t("opsAdmin.suppliers.title")}
          </Link>
        </div>
      </div>

      <FacetedListLayout
        searchPlaceholder={t("opsAdmin.components.searchPlaceholder")}
        total={total}
        limit={limit}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title={t("opsAdmin.common.kind")}
              paramKey="kind"
              order={{ enum: SERVICE_KINDS }}
              buckets={facets.kind ?? []}
              labelFor={(v) => enumLabel(SERVICE_KIND_LABELS, v as ServiceKind)}
            />
            <FacetPanel
              title={t("opsAdmin.common.supplier")}
              paramKey="supplierId"
              order="name"
              buckets={facets.supplierId ?? []}
              labelFor={supName}
              nullLabel={t("opsAdmin.common.noSupplier")}
            />
            <FacetPanel
              title={t("common.fields.destination")}
              paramKey="destinationId"
              order="name"
              buckets={facets.destinationId ?? []}
              labelFor={destName}
              nullLabel={t("opsAdmin.common.global")}
            />
            <FacetPanel
              title={t("opsAdmin.components.costScope")}
              paramKey="costScope"
              order={{ enum: COST_SCOPES }}
              buckets={facets.costScope ?? []}
              labelFor={(v) => enumLabel(COST_SCOPE_LABELS, v as CostScope)}
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {rows.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-gray-500">
              <Package className="mb-2 h-10 w-10 text-gray-300" />
              <p>{t("opsAdmin.components.empty")}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  {[
                    t("opsAdmin.components.colComponent"),
                    t("opsAdmin.common.kind"),
                    t("opsAdmin.common.supplier"),
                    t("opsAdmin.components.colCost"),
                    t("opsAdmin.components.colSell"),
                    t("opsAdmin.components.colMargin"),
                    t("opsAdmin.components.colScope"),
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((r) => {
                  const margin = r.sellPrice - r.cost;
                  return (
                    <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/supplier-components/${r.id}`}
                          className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                        >
                          {r.name}
                        </Link>
                        {!r.isActive && (
                          <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                            {t("common.status.inactive")}
                          </span>
                        )}
                        {r.destinationName && (
                          <p className="text-xs text-gray-400">{r.destinationName}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {enumLabel(SERVICE_KIND_LABELS, r.kind)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {r.supplierName || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {formatPrice(r.cost, r.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {formatPrice(r.sellPrice, r.currency)}
                      </td>
                      <td
                        className={`px-4 py-3 text-sm font-medium ${
                          margin >= 0
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-red-600 dark:text-red-400"
                        }`}
                      >
                        {formatPrice(margin, r.currency)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {enumLabel(COST_SCOPE_LABELS, r.costScope)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
